"use client";

import { QuerySelect, QuerySelectOption } from "@/components/query-select";
import { Translation } from "@/data/lang";
import { useUpdateQuery } from "@/hooks/use-update-query";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { ButtonGroup, ButtonGroupOption } from "ui/core/button-group";

import {
  LargeThresholdShares,
  Period,
  SmallThresholdShares,
} from "../../types/shared";

const getLargeThresholdOptions = (): ButtonGroupOption[] => [
  { label: "<100", value: "100" },
  { label: "200", value: "200" },
  { label: "400", value: "400" },
  { label: "600", value: "600" },
  { label: "800", value: "800" },
  { label: "1000", value: "1000" },
];

const getSmallThresholdOptions = (): ButtonGroupOption[] => [
  { label: ">10", value: "10" },
  { label: "20", value: "20" },
  { label: "30", value: "30" },
  { label: "40", value: "40" },
  { label: "50", value: "50" },
  { label: "100", value: "100" },
];

const getPeriodOptions = ({ t }: { t: Translation }): QuerySelectOption[] => [
  {
    label: `10 ${t.analysis.chip.shareholderDiversification.weeks}`,
    value: "10",
  },
  {
    label: `30 ${t.analysis.chip.shareholderDiversification.weeks}`,
    value: "30",
  },
  {
    label: `60 ${t.analysis.chip.shareholderDiversification.weeks}`,
    value: "60",
  },
];

export const ShareholderDiversificationControlPanel = ({
  lang,
  period,
  largeThresholdShares,
  smallThresholdShares,
}: {
  lang: Lang;
  period: Period;
  largeThresholdShares: LargeThresholdShares;
  smallThresholdShares: SmallThresholdShares;
}) => {
  const { t } = getTranslations(lang);
  const { updateQuery } = useUpdateQuery();
  const largeThresholdSharesOptions = getLargeThresholdOptions();
  const smallThresholdSharesOptions = getSmallThresholdOptions();
  const periodOptions = getPeriodOptions({ t });

  const onButtonClick =
    ({ queryName }: { queryName: string }) =>
    (value: string) => {
      updateQuery({ name: queryName, value });
    };

  return (
    <div className="space-y-4">
      <div className="flex w-full max-w-[800px]  flex-wrap items-center gap-4">
        <p>{t.analysis.chip.shareholderDiversification.largeThreshold}</p>
        <ButtonGroup
          value={largeThresholdShares}
          options={largeThresholdSharesOptions}
          onButtonClick={onButtonClick({ queryName: "large-threshold-shares" })}
          className="flex-auto"
        />
      </div>
      <div className="flex w-full max-w-[800px] flex-wrap items-center gap-4">
        <p>{t.analysis.chip.shareholderDiversification.smallThreshold}</p>
        <ButtonGroup
          value={smallThresholdShares}
          options={smallThresholdSharesOptions}
          onButtonClick={onButtonClick({ queryName: "small-threshold-shares" })}
          className="flex-auto"
        />
      </div>
      <h3 className="pt-6">
        {t.analysis.chip.shareholderDiversification.tradingStatus}
      </h3>
      <QuerySelect
        options={periodOptions}
        defaultLabel={
          periodOptions.find((option) => option.value === period)?.label
        }
        queryName="shareholder-diversification-period"
        className="max-w-[180px]"
      />
    </div>
  );
};
